import { useCallback, useEffect } from "react"

import eq from "lodash-es/eq"
import isEmpty from "lodash-es/isEmpty"
import isNumber from "lodash-es/isNumber"
import last from "lodash-es/last"

import { generateUuid, reduxBatch } from "@/3514/utils"

import { useCoachingChatSocketReceiver as useSocketReceiver } from "../api"
import {
    setCoachingChatConversationApiMode as setConversationApiMode,
    useCoachingChatConversationApiMode as useConversationApiMode,
    useCoachingChatConversationDispatcher as useConversationDispatcher,
    useCoachingChatConversationIdSelector as useConversationIdSelector
} from "../store"
import {
    ECoachingChatConversationApiMode as EConversationApiMode,
    ECoachingChatConversationMessagePhase as EConversationMessagePhase,
    ECoachingChatEntityMode as EEntityMode,
    ECoachingChatMessageAuthor as EMessageAuthor,
    ECoachingChatMessageTransport as EMessageTransport,
    ECoachingChatSocketListenerEventName as ESocketListenerEventName,
    type TCoachingChatConversationMessage as TConversationMessage,
    type TCoachingChatSocketHistoryEventData as THistoryEventData,
    type TCoachingChatSocketHistoryEventResponse as THistoryEventResponse,
    type _TCoachingChatHookSharedParams as _TParams
} from "../types"
import { coachingChatUiUtils as uiUtils } from "../utils"
import { logCoachingChatState as logChatState } from "../utils/coaching-chat.diagnostics"

const {
    streamingUtils: { adaptHistoryMessageToUiMessages: historyMessagesAdapter }
} = uiUtils

function useHistoryReceiver({ entityId, entityMode, wasChatCompleted }: _TParams): void {
    const conversationDispatcher: ReturnType<typeof useConversationDispatcher> = useConversationDispatcher(entityId)

    const conversationId: number | null = useConversationIdSelector(entityId)

    const conversationApiMode: EConversationApiMode = useConversationApiMode()

    const isEnabled: boolean =
        eq(conversationApiMode, EConversationApiMode.Sockets) &&
        eq(entityMode, EEntityMode.Full) &&
        !wasChatCompleted &&
        isNumber(conversationId)

    const handler: (resp: THistoryEventData[]) => void = useCallback(
        (resp: THistoryEventData[]): void => {
            // History lives in Redis and is appended after streaming finishes, so it can lag the
            // conversation the participant already sees. When the last entry is a user message,
            // the branch below appends a "waiting" bubble — that is the owl that types forever.
            // This also replaces summary/status wholesale, which is how a finished chat comes back
            // looking active with its summary gone.
            logChatState("history", {
                entityId,
                // `applied` must mean "the feed was rebuilt from this payload" — an empty replay is
                // ignored below, and reporting it as applied made the trace read as if the server
                // had legitimately returned an empty conversation
                applied: !isEmpty(resp),
                ignoredReason: isEmpty(resp) ? "emptyPayload" : null,
                messagesCount: resp?.length ?? 0,
                lastAuthorRole: isEmpty(resp) ? null : last<THistoryEventData>(resp).role,
                conversationId
            })

            setConversationApiMode(EConversationApiMode.Sockets)

            const tempMessage: TConversationMessage = {
                id: 1,
                transport: EMessageTransport.Sockets,
                author: EMessageAuthor.AI,
                content: String(""),
                isStreamable: true,
                status: EConversationMessagePhase.Waiting,
                clientMessageId: generateUuid(),
                conversationId
            }

            // An empty payload means the replay had nothing to offer — the conversation may still
            // be mid-generation, or its Redis history may have expired. It is never evidence that
            // the conversation is empty, so wiping the rendered feed for it (which is what
            // replacing the messages with a lone placeholder used to do) just blanks the chat.
            if (isEmpty(resp)) return void 0

            reduxBatch((): void => {
                // messages only: summary, status and id belong to the conversation, and history
                // knows nothing about them
                conversationDispatcher.loadHistory(historyMessagesAdapter(resp, conversationId))

                if (eq(last<THistoryEventData>(resp).role, EMessageAuthor.User)) {
                    conversationDispatcher.addMessage(tempMessage)
                    conversationDispatcher.markInput({ isDisabled: true, isDimmed: true })
                } else conversationDispatcher.markInput({ isDisabled: false, isDimmed: false })
            })

            return void 0
        },
        [conversationId, conversationDispatcher, entityId]
    )

    // The listener is only attached while `isEnabled` holds. The server replies to cm_join within
    // milliseconds, so if this flips to true *after* that reply lands, the history event is
    // dropped on the floor with nothing to show for it. This trace is how you tell "no history was
    // sent" apart from "history was sent while we were not listening".
    useEffect((): void => {
        logChatState("history", {
            entityId,
            applied: false,
            listenerEnabled: isEnabled,
            conversationApiMode,
            wasChatCompleted,
            conversationId
        })
    }, [isEnabled, conversationApiMode, wasChatCompleted, conversationId, entityId])

    useSocketReceiver<THistoryEventResponse>({
        isEnabled,
        event: ESocketListenerEventName.History,
        handler
    })
}

export { useHistoryReceiver as useCoachingChatHistoryReceiver }
